<template>
	<div id="settlementHub">
		<page-header title="结算中心" :count="searchForm.total" unit="个">
			<el-input v-model.trim="searchForm.projectName" class="ph-search"
				placeholder="搜索项目..." prefix-icon="el-icon-search" clearable size="small"
				@input="debouncedSearch" />
		</page-header>

		<!-- 项目列表 -->
		<div class="project-list" v-loading="loading">
			<div class="project-row" v-for="item in pList" :key="item.id">
				<!-- 第一行：类型 + 项目名 -->
				<div class="row-main">
					<span class="type-tag" :class="item.projectMeetingType === '1' || item.projectMeetingType === 'series' ? 'series' : 'single'">
						{{ item.projectMeetingType === '1' || item.projectMeetingType === 'series' ? '系列会' : '单场会' }}
					</span>
					<h3 class="project-name">{{ item.projectName }}</h3>
				</div>
				<!-- 第二行：人员信息 + 操作按钮 -->
				<div class="row-sub">
					<div class="meta-info">
						<span class="meta-item">
							<i class="el-icon-user"></i>
							助教：<b>{{ item.actuatorName || '-' }}</b>
						</span>
						<span class="meta-divider"></span>
						<span class="meta-item">
							<i class="el-icon-s-custom"></i>
							官员：<b>{{ item.projectManager || '-' }}</b>
						</span>
					</div>
					<div class="action-group">
						<!-- 供应商结算：助教 + 管理员可见 -->
						<div class="action-btn supplier" v-if="canSeeSupplier" @click="submitSupplier(item)">
							<i class="el-icon-box"></i> 供应商结算
						</div>
						<!-- 劳务结算：结算官员 + 管理员可见 -->
						<div class="action-btn labor" v-if="canSeeLabor" @click="submitLabor(item)">
							<i class="el-icon-user"></i> 劳务结算
						</div>
						<!-- 审核记录：所有可见用户 -->
						<div class="action-btn ghost" @click="viewAuditRecord(item)">
							<i class="el-icon-tickets"></i> 审核记录
						</div>
					</div>
				</div>
			</div>

			<!-- 空状态 -->
			<div class="empty-state" v-if="!loading && !pList.length">
				<i class="el-icon-folder-opened"></i>
				<p>暂无项目</p>
			</div>
		</div>

		<!-- 分页 -->
		<div class="app-pagination" v-if="searchForm.total > searchForm.size">
			<el-pagination background layout="prev, pager, next, total"
				:total="searchForm.total" :page-size="searchForm.size" :current-page.sync="searchForm.current"
				@current-change="handlePage" />
		</div>

		<!-- 供应商结算弹窗 -->
		<el-dialog v-if="settleAccountsShow" :visible.sync="settleAccountsShow" title="选择收款方式" width="480px" center
			custom-class="modern-dialog">
			<el-form ref="form" :model="accountForm" label-position="top">
				<el-form-item label="收款方式">
					<el-select v-model="accountForm.collectWay" placeholder="请选择" style="width: 100%">
						<el-option v-for="(item, i) in collectWayEnums" :key="i" :label="item.label"
							:value="item.value"></el-option>
					</el-select>
				</el-form-item>
				<el-form-item label="费用类型">
					<el-select v-model="accountForm.feeType" placeholder="请选择" style="width: 100%">
						<el-option label="会议类" value="0"></el-option>
						<el-option label="非会议类" value="1" disabled></el-option>
					</el-select>
				</el-form-item>
			</el-form>
			<div class="tips-card">
				<div class="tips-title"><i class="el-icon-info"></i> 收款方式说明</div>
				<ul>
					<li><b>一次性收款</b> — 选择"一次性收款并结项"</li>
					<li><b>两次收款</b> — 首次收款 → 尾款</li>
					<li><b>三次及以上</b> — 首次 → 阶段收款 → 尾款</li>
					<li><b>据实结算</b> — 按场次收款，可多次申请</li>
				</ul>
			</div>
			<span slot="footer">
				<el-button @click="settleAccountsShow = false">取消</el-button>
				<el-button type="primary" @click="toSupplierSettlementNotOrderId"
					:disabled="!accountForm.collectWay">确定</el-button>
			</span>
		</el-dialog>

		<!-- 审核记录弹窗 -->
		<el-dialog v-if="auditDialogShow" :visible.sync="auditDialogShow" title="审核记录" width="700px"
			custom-class="modern-dialog">
			<div class="audit-project-badge">{{ currentProject.projectName }}</div>
			<div v-loading="auditLoading">
				<div class="audit-list" v-if="auditOrderList.length > 0">
					<div class="audit-item" v-for="order in auditOrderList" :key="order.id">
						<div class="audit-type">
							<el-tag size="mini" :type="order.settlementType === 'LABOR' ? 'warning' : ''">
								{{ order.settlementType === 'LABOR' ? '劳务' : '供应商' }}
							</el-tag>
						</div>
						<div class="audit-left">
							<span class="audit-no">JS-{{ order.id }}</span>
							<span class="audit-way">{{ collectWayLabel(order.collectWay) }}</span>
						</div>
						<div class="audit-center">
							<span class="audit-money">¥{{ order.applyMoney || order.orderAmount || 0 }}</span>
						</div>
						<div class="audit-right">
							<el-tag size="mini" :type="auditStatusType(order.status)">
								{{ auditStatusLabel(order.status) }}
							</el-tag>
							<span class="audit-time">{{ order.createTime || '-' }}</span>
						</div>
						<div class="audit-action" @click="toSettlementDetail(order)">
							详情 <i class="el-icon-arrow-right"></i>
						</div>
					</div>
				</div>
				<el-empty v-else description="暂无结算记录" :image-size="80"></el-empty>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import { getProjectListNew as fetchProjectList, getLatestOrderByProject, getCollectWayOptions } from '@/request/api.js';
import { isOfficialRole } from '@/config/menu.js';
import PageHeader from '@/components/PageHeader.vue';

export default {
	name: 'SettlementCenter',
	components: { PageHeader },
	data() {
		return {
			loading: false,
			searchForm: { total: 0, current: 1, size: 10, projectName: '' },
			pList: [],
			searchTimer: null,
			settleAccountsShow: false,
			projectId: '',
			orderData: {},
			collectWayEnums: [],
			accountForm: { feeType: '0', collectWay: '' },
			auditDialogShow: false,
			auditLoading: false,
			currentProject: {},
			auditOrderList: [],
			userRole: '',
		}
	},
	computed: {
		/** 助教 + 管理员可看供应商结算 */
		canSeeSupplier() {
			return this.userRole === 'ADMIN' || this.userRole === 'ASSISTANT';
		},
		/** 结算官员 + 管理员可看劳务结算 */
		canSeeLabor() {
			return this.userRole === 'ADMIN' || this.userRole === 'OFFICIAL_SETTLEMENT';
		},
		/** 审核记录过滤类型 */
		auditTypeFilter() {
			if (this.userRole === 'ASSISTANT') return 'SUPPLIER';
			if (this.userRole === 'OFFICIAL_SETTLEMENT') return 'LABOR';
			return null; // ADMIN 看全部
		}
	},
	created() {
		const token = JSON.parse(localStorage.getItem('token'));
		this.userRole = token?.type || '';
		this.getProjectList();
	},
	methods: {
		handlePage(page) { this.searchForm.current = page; this.getProjectList(); },
		debouncedSearch() {
			if (this.searchTimer) clearTimeout(this.searchTimer);
			this.searchTimer = setTimeout(() => { this.searchForm.current = 1; this.getProjectList(); }, 400);
		},
		getProjectList() {
			this.loading = true;
			fetchProjectList(this.searchForm).then(res => {
				if (res.code == 200) { this.pList = res.data.records; this.searchForm.total = res.data.total; }
				this.loading = false;
			}).catch(() => { this.loading = false; });
		},

		// ==================== 供应商结算 ====================
		submitSupplier(item) {
			this.projectId = item.id;
			this.getCollectWayFun();
			getLatestOrderByProject(item.id).then(res => {
				this.orderData = res.data;
				if (this.orderData) {
					const { status, collectWay } = this.orderData;
					if (status === 'SETTLED' && (collectWay == 1 || collectWay == 2)) {
						this.settleAccountsShow = true;
					} else { this.toSupplierSettlement(); }
				} else { this.settleAccountsShow = true; }
			});
		},
		toSupplierSettlement() {
			this.$router.push({ name: 'settlementEdit', query: { orderId: this.orderData.id } });
		},
		toSupplierSettlementNotOrderId() {
			this.$router.push({ name: 'settlementEdit', query: { pId: this.projectId, collectWay: this.accountForm.collectWay, feeType: this.accountForm.feeType } });
		},
		getCollectWayFun() {
			getCollectWayOptions().then(res => {
				if (res.code == 200) { this.collectWayEnums = res.data; if (this.collectWayEnums.length === 1) this.accountForm.collectWay = this.collectWayEnums[0].value; }
			});
		},

		// ==================== 劳务结算 ====================
		submitLabor(item) {
			this.$router.push({ name: 'laborSettlement', query: { pId: item.id, projectName: item.projectName } });
		},

		// ==================== 审核记录 ====================
		viewAuditRecord(item) {
			this.currentProject = item;
			this.auditDialogShow = true;
			this.auditLoading = true;
			this.auditOrderList = [];
			// TODO: 换成查询全部结算单的 API（带 settlementType 过滤）
			getLatestOrderByProject(item.id).then(res => {
				if (res.data) {
					let list = Array.isArray(res.data) ? res.data : [res.data];
					// 按角色过滤
					if (this.auditTypeFilter) {
						list = list.filter(o => (o.settlementType || 'SUPPLIER') === this.auditTypeFilter);
					}
					this.auditOrderList = list;
				}
				this.auditLoading = false;
			}).catch(() => { this.auditLoading = false; });
		},
		toSettlementDetail(order) {
			if (order.settlementType === 'LABOR') {
				this.$router.push({ name: 'laborSettlement', query: { orderId: order.id } });
			} else {
				this.$router.push({ name: 'settlementEdit', query: { orderId: order.id } });
			}
		},

		// ==================== 工具 ====================
		collectWayLabel(v) { return { '0': '一次性收款', '1': '首次收款', '2': '阶段收款', '3': '尾款', '4': '据实结算' }[v] || '-'; },
		auditStatusType(s) { return { WAIT: 'warning', SUBMIT: '', ERROR: 'danger', SUCCESS: 'success', FINISH: 'success', SETTLED: 'success' }[s] || 'info'; },
		auditStatusLabel(s) { return { WAIT: '待提交', SUBMIT: '审核中', ERROR: '已驳回', SUCCESS: '已通过', FINISH: '已打款', SETTLED: '已结算' }[s] || s || '-'; },
	}
}
</script>

<style lang="scss" scoped>
#settlementHub {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	padding: 32px 40px;
	background: #f0f2f8;

	/* .page-header 已抽到 PageHeader 组件 */

	.project-list { flex: 1; }

	.project-row {
		background: #fff;
		border-radius: 10px;
		padding: 10px 16px;
		margin-bottom: 6px;
		border: 1px solid #eef0f6;
		transition: all 0.2s ease;

		&:hover {
			border-color: #667eea;
			box-shadow: 0 2px 8px rgba(102, 126, 234, 0.08);
		}

		.row-main {
			display: flex;
			align-items: center;
			gap: 12px;
			margin-bottom: 10px;

			.type-tag {
				font-size: 11px;
				font-weight: 700;
				padding: 3px 10px;
				border-radius: 6px;
				flex-shrink: 0;

				&.series { background: rgba(5, 150, 105, 0.08); color: #059669; }
				&.single { background: rgba(99, 102, 241, 0.08); color: #6366f1; }
			}

			.project-name {
				font-size: 15px;
				font-weight: 600;
				color: #1a1a2e;
				margin: 0;
				flex: 1;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}

		.row-sub {
			display: flex;
			align-items: center;
			justify-content: space-between;

			.meta-info {
				display: flex;
				align-items: center;
				gap: 6px;
				font-size: 13px;
				color: #8f9bb3;

				i { font-size: 13px; margin-right: 2px; }
				b { color: #4a5568; font-weight: 600; }

				.meta-divider {
					width: 1px;
					height: 12px;
					background: #e2e8f0;
					margin: 0 6px;
				}
			}

			.action-group {
				display: flex;
				gap: 8px;
			}

			.action-btn {
				font-size: 13px;
				font-weight: 600;
				display: flex;
				align-items: center;
				gap: 4px;
				padding: 5px 14px;
				border-radius: 8px;
				cursor: pointer;
				transition: all 0.2s;

				&.supplier {
					color: #667eea;
					background: rgba(102, 126, 234, 0.08);
					&:hover { background: rgba(102, 126, 234, 0.16); }
				}

				&.labor {
					color: #e6a23c;
					background: rgba(230, 162, 60, 0.08);
					&:hover { background: rgba(230, 162, 60, 0.16); }
				}

				&.ghost {
					color: #8f9bb3;
					&:hover { color: #667eea; background: rgba(102, 126, 234, 0.06); }
				}

				i { font-size: 13px; }
			}
		}
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 80px 20px;
		color: #a8a29e;

		i { font-size: 48px; margin-bottom: 12px; }
		p { font-size: 14px; }
	}

	.pagination {
		margin: 20px auto;
		text-align: center;
	}
}

/* 弹窗 */
::v-deep .modern-dialog {
	border-radius: 14px;
	overflow: hidden;

	.el-dialog__header {
		padding: 18px 24px;
		border-bottom: 1px solid #f0f0f5;
		.el-dialog__title { font-weight: 700; font-size: 16px; color: #1a1a2e; }
	}
	.el-dialog__body { padding: 20px 24px; }
	.el-dialog__footer { padding: 12px 24px 20px; }
}

.tips-card {
	background: #f5f7ff;
	border-radius: 10px;
	padding: 14px 18px;
	margin-top: 4px;

	.tips-title {
		font-size: 13px;
		font-weight: 700;
		color: #667eea;
		margin-bottom: 8px;
		i { margin-right: 4px; }
	}

	ul {
		margin: 0;
		padding-left: 16px;
		li {
			font-size: 12px;
			color: #606266;
			line-height: 2;
			b { color: #303133; }
		}
	}
}

.audit-project-badge {
	font-size: 14px;
	font-weight: 600;
	color: #667eea;
	margin-bottom: 16px;
	padding: 8px 14px;
	background: #f5f7ff;
	border-radius: 8px;
	display: inline-block;
}

.audit-list {
	.audit-item {
		display: flex;
		align-items: center;
		padding: 14px 16px;
		border-radius: 10px;
		margin-bottom: 8px;
		background: #fafbfe;
		border: 1px solid #eef0f6;
		transition: all 0.15s;

		&:hover { border-color: #667eea; background: #f5f7ff; }

		.audit-type {
			margin-right: 12px;
			flex-shrink: 0;
		}

		.audit-left {
			display: flex;
			align-items: center;
			gap: 10px;
			min-width: 160px;

			.audit-no { font-family: monospace; font-size: 12px; color: #8f9bb3; }
			.audit-way { font-size: 13px; color: #4a5568; font-weight: 500; }
		}

		.audit-center {
			flex: 1;
			.audit-money { color: #e6a23c; font-weight: 700; font-size: 15px; }
		}

		.audit-right {
			display: flex;
			align-items: center;
			gap: 12px;
			.audit-time { font-size: 12px; color: #a0aec0; }
		}

		.audit-action {
			margin-left: 16px;
			font-size: 13px;
			font-weight: 600;
			color: #667eea;
			cursor: pointer;
			display: flex;
			align-items: center;
			gap: 2px;
			padding: 4px 10px;
			border-radius: 6px;
			transition: background 0.15s;

			&:hover { background: rgba(102, 126, 234, 0.1); }
			i { font-size: 12px; }
		}
	}
}
</style>
